/**
 * useSecretStorage - React hook for Secret Storage operations
 *
 * Provides:
 * - SSSS session management
 * - Cross-signing secrets retrieval
 * - Megolm backup key access
 * - Loading and error states
 * - Integration with RecoveryContext
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import secretStorageService from '../services/SecretStorageService';
import { useRecovery } from '../context/RecoveryContext/index';
import recoveryEphemeralStore from '../services/recoveryEphemeralStore';
// RecoveryKeyStorage removed - only use ephemeral storage for security
import logger from '../utils/logger';

/**
 * Hook for managing Secret Storage operations
 * @returns {Object} SSSS state and actions
 */
export function useSecretStorage() {
  // New RecoveryContext returns state directly
  const recoveryState = useRecovery();

  // State
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [secrets, setSecrets] = useState({
    crossSigning: null,
    backupKey: null,
  });

  // Refs for cleanup
  const cleanupRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Open SSSS with recovery key
  const open = useCallback(async (recoveryKey = null) => {
    if (isOpen) {
      logger.debug('useSecretStorage: SSSS already open');
      return session;
    }

    setIsLoading(true);
    setError(null);

    try {
      let keyToUse = recoveryKey;

      // Try to get recovery key from ephemeral store if not provided
      if (!keyToUse) {
        // Only check ephemeral store - we don't persist recovery keys
        const ephemeralKey = recoveryEphemeralStore.getKey();
        if (ephemeralKey) {
          logger.debug('useSecretStorage: Using key from ephemeral store');
          keyToUse = ephemeralKey;
        }
      }

      if (!keyToUse) {
        throw new Error('No recovery key available');
      }

      // Open SSSS
      const newSession = await secretStorageService.openWithRecoveryKey(keyToUse);

      setSession(newSession);
      setIsOpen(true);
      logger.debug('useSecretStorage: SSSS opened successfully', {
        keyId: newSession.keyId,
        version: newSession.version,
      });

      return newSession;

    } catch (err) {
      logger.error('useSecretStorage: Failed to open SSSS:', err.message);
      setError({
        code: err.code || 'OPEN_FAILED',
        message: err.message || 'Failed to open secret storage',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, session]);

  // Close SSSS session
  const close = useCallback(() => {
    secretStorageService.close();
    setIsOpen(false);
    setSession(null);
    setSecrets({
      crossSigning: null,
      backupKey: null,
    });
    setError(null);
    logger.debug('useSecretStorage: SSSS session closed');
  }, []);

  // Get a specific secret
  const getSecret = useCallback(async (secretName) => {
    if (!isOpen) {
      throw new Error('SSSS not open');
    }

    try {
      const secret = await secretStorageService.getSecret(secretName);
      logger.debug(`useSecretStorage: Retrieved secret ${secretName}`);
      return secret;
    } catch (err) {
      logger.error(`useSecretStorage: Failed to get secret ${secretName}:`, err.message);
      throw err;
    }
  }, [isOpen]);

  // Store a secret
  const storeSecret = useCallback(async (secretName, secretData) => {
    if (!isOpen) {
      throw new Error('SSSS not open');
    }

    try {
      await secretStorageService.storeSecret(secretName, secretData);
      logger.debug(`useSecretStorage: Stored secret ${secretName}`);
    } catch (err) {
      logger.error(`useSecretStorage: Failed to store secret ${secretName}:`, err.message);
      throw err;
    }
  }, [isOpen]);

  // Retrieve cross-signing secrets
  const retrieveCrossSigningSecrets = useCallback(async () => {
    if (!isOpen) {
      await open();
    }

    setIsLoading(true);
    setError(null);

    try {
      const crossSigningSecrets = await secretStorageService.retrieveCrossSigningSecrets();

      setSecrets(prev => ({
        ...prev,
        crossSigning: crossSigningSecrets,
      }));

      logger.debug('useSecretStorage: Retrieved cross-signing secrets', {
        hasM

: !!crossSigningSecrets.master,
        hasSelf: !!crossSigningSecrets.self_signing,
        hasUser: !!crossSigningSecrets.user_signing,
      });

      return crossSigningSecrets;

    } catch (err) {
      logger.error('useSecretStorage: Failed to retrieve cross-signing secrets:', err.message);
      setError({
        code: err.code || 'RETRIEVE_FAILED',
        message: err.message || 'Failed to retrieve cross-signing secrets',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, open]);

  // Retrieve megolm backup key
  const retrieveBackupKey = useCallback(async () => {
    if (!isOpen) {
      await open();
    }

    setIsLoading(true);
    setError(null);

    try {
      const backupKey = await secretStorageService.retrieveMegolmBackupKey();

      setSecrets(prev => ({
        ...prev,
        backupKey,
      }));

      logger.debug('useSecretStorage: Retrieved megolm backup key', {
        hasKey: !!backupKey,
      });

      return backupKey;

    } catch (err) {
      logger.error('useSecretStorage: Failed to retrieve backup key:', err.message);
      setError({
        code: err.code || 'RETRIEVE_FAILED',
        message: err.message || 'Failed to retrieve backup key',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, open]);

  // Bootstrap SSSS for new setup
  // @param {Uint8Array} recoveryKeyBytes - Recovery key bytes
  // @param {Object} options - Options including onPasswordRequired callback
  const bootstrap = useCallback(async (recoveryKeyBytes = null, options = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      // Generate new recovery key if not provided
      if (!recoveryKeyBytes) {
        const keyArray = new Uint8Array(32);
        crypto.getRandomValues(keyArray);
        recoveryKeyBytes = keyArray;
      }

      const result = await secretStorageService.bootstrapSSSS(recoveryKeyBytes, options);

      setSession(result);
      setIsOpen(true);

      logger.debug('useSecretStorage: SSSS bootstrapped', {
        keyId: result.keyId,
        crossSigningConfigured: result.crossSigningConfigured,
      });

      return result;

    } catch (err) {
      logger.error('useSecretStorage: Failed to bootstrap SSSS:', err.message);
      setError({
        code: err.code || 'BOOTSTRAP_FAILED',
        message: err.message || 'Failed to bootstrap secret storage',
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check if a secret exists
  const hasSecret = useCallback(async (secretName) => {
    try {
      return await secretStorageService.hasSecret(secretName);
    } catch (err) {
      logger.error(`useSecretStorage: Failed to check secret ${secretName}:`, err.message);
      return false;
    }
  }, []);

  // Setup event listeners
  useEffect(() => {
    const handleStateChange = (event) => {
      logger.debug('useSecretStorage: State change', event);

      if (event.state === 'open') {
        setIsOpen(true);
      } else if (event.state === 'closed') {
        setIsOpen(false);
        setSession(null);
      } else if (event.state === 'error') {
        setError(event.error);
      }
    };

    const handleError = (error) => {
      logger.error('useSecretStorage: Service error', error);
      setError(error);
    };

    secretStorageService.on('state', handleStateChange);
    secretStorageService.on('error', handleError);

    // Cleanup function
    cleanupRef.current = () => {
      secretStorageService.off('state', handleStateChange);
      secretStorageService.off('error', handleError);
    };

    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, []);

  // Auto-open if recovery is complete
  useEffect(() => {
    if (recoveryState.needsRecovery === false && !isOpen && !isLoading) {
      // Try to open automatically after successful recovery
      const autoOpen = async () => {
        try {
          await open();
        } catch (err) {
          // Silent fail for auto-open
          logger.debug('useSecretStorage: Auto-open failed (expected):', err.message);
        }
      };

      autoOpen();
    }
  }, [recoveryState.needsRecovery, isOpen, isLoading, open]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Don't close SSSS on unmount as it's a singleton service
      // Other components might still need it
    };
  }, []);

  return {
    // State
    isOpen,
    isLoading,
    error,
    session,
    secrets,

    // Actions
    open,
    close,
    getSecret,
    storeSecret,
    retrieveCrossSigningSecrets,
    retrieveBackupKey,
    bootstrap,
    hasSecret,

    // Metadata
    keyInfo: session?.keyInfo,
    version: session?.versionInfo,
  };
}

/**
 * Hook for using SSSS within recovery flow
 * Simplified version that integrates with RecoveryContext
 */
export function useSSSSRecovery() {
  // New RecoveryContext returns state directly
  const recoveryState = useRecovery();
  const ssss = useSecretStorage();

  // Open SSSS when recovery key is submitted
  const openWithRecoveryKey = useCallback(async (recoveryKey) => {
    try {
      // Store the key in ephemeral store (replaces old storeRecoveryKey)
      recoveryEphemeralStore.setKey(recoveryKey);
      recoveryEphemeralStore.persistToSession();

      // Open SSSS
      const session = await ssss.open(recoveryKey);

      // Automatically retrieve secrets
      const [crossSigning, backupKey] = await Promise.allSettled([
        ssss.retrieveCrossSigningSecrets(),
        ssss.retrieveBackupKey(),
      ]);

      logger.debug('useSSSSRecovery: Auto-retrieved secrets', {
        crossSigning: crossSigning.status,
        backupKey: backupKey.status,
      });

      return {
        session,
        crossSigning: crossSigning.status === 'fulfilled' ? crossSigning.value : null,
        backupKey: backupKey.status === 'fulfilled' ? backupKey.value : null,
      };

    } catch (err) {
      logger.error('useSSSSRecovery: Failed to open with recovery key:', err.message);
      throw err;
    }
  }, [ssss]);

  return {
    ...ssss,
    openWithRecoveryKey,
    needsRecovery: recoveryState.needsRecovery,
    hasBackup: recoveryState.hasBackup,
    // New context doesn't track hasSSSS separately - use ssss.isOpen as proxy
    hasSSSS: ssss.isOpen,
  };
}